import { defineStore } from 'pinia'
import { ApiRes, ApiWalletResponse, BankAccount, getLegalWallet, getNaturalWallet, MoneyWithdrawal } from '../api'
import { TransactionMethod, TransactionStatus, TransactionType, WalletStatus } from '../../typings'
import { isLegalProfile } from '../investment-process/useUserProfileType'
import { useLegalAccountDataState } from './useAccountDataLegal'

export interface Wallet {
    id: string
    status: WalletStatus
    balance: number
    retrievable_balance: number
    currency: string
    currency_symbol: string
    deposit_account_holder: string
    deposit_payment_reference: string
    deposit_iban: string
    deposit_bic: string
    limit_credit_card: number
    limit_bank_transfer: number
}

export interface WalletTransaction {
    id: string
    type: TransactionType
    date: string | Date
    from: string
    to: string
    amount: number
    signed_amount: number
    status: string
    internal_status: string
    internal_status_text: string
    method: TransactionMethod
    order_id: string
    asset_title: string
    transaction_purpose_text: string
}

export interface WalletState {
    wallet?: Wallet | null
    transactions: WalletTransaction[]
    bankAccounts: BankAccount[]
    moneyWithdrawals: MoneyWithdrawal[]
    loaded: boolean
    loading: boolean
    saving: boolean

    fetchWalletData?(): Promise<void>
    fetchWalletDataSilent?(): Promise<void>
    filteredTransactions?: WalletTransaction[]
    verifiedBankAccounts?: BankAccount[]
}

export const useWalletState = defineStore('wallet-data', {
    state: (): WalletState => {
        return {
            wallet: null,
            transactions: [],
            bankAccounts: [],
            moneyWithdrawals: [],
            loading: false,
            loaded: false,
            saving: false,
        }
    },
    actions: {
        async fetchWalletData() {
            this.loading = true
            try {
                const response = await getWallet()
                this.loading = false
                this.loaded = true
                this.$patch(response.data)
            } catch (error) {
                console.log(error)
            } finally {
                this.loading = false
            }
        },
        async fetchWalletDataSilent() {
            try {
                const response = await getWallet()
                this.$patch(response.data)
            } catch (error) {
                console.log(error)
            }
        },
    },
    getters: {
        filteredTransactions(): WalletTransaction[] {
            return this.transactions.filter(
                (transaction) =>
                    transaction.status === TransactionStatus.SUCCESS_MONEY_IN_OUT_OR_PENDING_P2P ||
                    transaction.status === TransactionStatus.SUCCESS_P2P_OR_MONEY_IN_OUT_ERROR
            )
        },
        verifiedBankAccounts(): BankAccount[] {
            return this.bankAccounts.filter((account) => account.status === 'verified')
        },
    },
})

const getWallet = (): ApiRes<ApiWalletResponse> => {
    if (isLegalProfile()) {
        const accountStateLegal = useLegalAccountDataState()
        accountStateLegal.fetchAccountData().finally(() => {
            const legalProfile = accountStateLegal.legalProfile
            console.log(legalProfile)
            return getLegalWallet(legalProfile.legal_profile_uid)
        })
    }
    return getNaturalWallet()
}
